import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { App, Badge, Button, Checkbox, Drawer, Grid, Input, Select } from 'antd';

import ContextBar from '../components/common/ContextBar';
import GroupHeader from '../components/common/GroupHeader';
import OverflowText from '../components/common/OverflowText';
import { RecordTileStack } from '../components/common/RecordBadge';
import PublishedBy from '../components/publishers/PublishedBy';
import InstalledBadge from '../components/discover/InstalledBadge';
import { FILTER, MAGNIFYING_GLASS } from '../components/icons';
import { discoverTiles, listingContentsLine, packageContentsLine, PACKAGE_KIND_LABELS } from '../services/packages';
import {
	KIND_OPTIONS, effectiveSort, facetCount, facetsToQuery, goesWithLine, loadBrowseRows, matchLine, publisherOptions,
	readFacets, requestSort, searchDiscover, sortOptions,
} from '../services/discoverSearch';
import { getCategories, sameCategory } from '../services/discover';
import { followCollection, listGlobalCollections, publicationRefusal, unfollowCollection } from '../services/publication';
import { formatDisplayDate } from '../utils/dateDisplay';
import { normalizeId } from '../utils/ids';
import { markdownToPlainText } from '../utils/markdown';
import { parseForBuilder, readSearchTerm, withSearchTerm } from '../utils/kqlBuilder';
import { serialize } from '../utils/kql';
import { NO_TEXT_ASSIST } from '../utils/fieldAssist';
import { packagePath } from '../utils/packageAddress';
import { DataState, Skeleton, deriveDataState } from '../components/loading';

const SEARCH_DEBOUNCE_MS = 250;

// KPR-387: Discover is curated. It opens with the Featured row an admin
// ordered, then one section per category in the admin's order; what carries a
// category the list no longer has is gathered under "Other" rather than lost.
// A package's kind (collection, bundle, card) is on its tile, not a heading:
// a person looks for "Home", not for "Card bundles".
const OTHER = 'Other';

/**
 * A tile's meta line: its parts joined by " · ", where a part may be a node
 * ("Published by keepr ✓" is one) as well as words. Empty parts drop out.
 */
function MetaLine({ parts }) {
	const shown = parts.filter(Boolean);
	return (
		<OverflowText className="card-def-card__meta meta-line">
			{shown.map((part, index) => (
				<React.Fragment key={index}>{index ? ' · ' : null}{part}</React.Fragment>
			))}
		</OverflowText>
	);
}

/**
 * What every tile on the page shares (KPR-1882). The title has the tile's whole
 * width, so what a package is and its stack of cards sit in the foot, below the
 * words, where no name competes with them. `children` is what stands at the
 * foot's far right: whether the person has it, or Follow.
 */
function TileFoot({ kind, records, label, version = null, children = null }) {
	return (
		<div className="discover-tile__foot">
			<RecordTileStack kind={kind} records={records} size="sm" />
			<div className="discover-tile__kind micro-label">{label}</div>
			{version ? <span className="discover-tile__version meta-line" title={`Version ${version}`}>v{version}</span> : null}
			{children}
		</div>
	);
}

/**
 * One package on the catalog — the card grid's tile, as a link. A
 * bundle's listing preset (keepr-api KPR-383) is a tile of its own: its name,
 * its description, its cards, and the add-ons it ticks — the same package,
 * opened with that listing.
 */
export function PackageTile({ pkg, listing = null, to = null, match = '' }) {
	const preview = Array.isArray(listing?.preview) ? listing.preview : (Array.isArray(pkg.preview) ? pkg.preview : []);
	const name = listing ? listing.name : pkg.name;
	const icon = listing?.icon || pkg.icon;
	const color = listing?.color || pkg.color;
	// A bundle is several cards, so its identity is a stack of them; a card
	// or a collection has one face.
	const records = pkg.kind === 'bundle' && preview.length > 1 ? preview : [{ icon, color, name }];
	const description = markdownToPlainText((listing ? listing.description : pkg.description) || '');
	// Who publishes it first (KPR-385): the one fact a person weighs before
	// what is inside. Absent on a package that names no publisher.
	const meta = [pkg.publisher ? <PublishedBy publisher={pkg.publisher} /> : null, listing ? listingContentsLine(pkg, listing) : packageContentsLine(pkg)];
	if (pkg.installs > 0) meta.push(pkg.installs === 1 ? 'Installed once' : `Installed ${pkg.installs.toLocaleString('en-US')} times`);
	return (
		<Link to={to || packagePath(pkg.key)} className="card-def-card card-def-card--interactive discover-tile">
			<OverflowText className="discover-tile__title">{name}</OverflowText>
			{description ? <OverflowText as="p" className="card-def-card__description">{description}</OverflowText> : null}
			<TileFoot kind={pkg.kind === 'collection' ? 'collection' : 'card'} records={records} label={PACKAGE_KIND_LABELS[pkg.kind] || 'Package'} version={pkg.version}>
				{/* Whether the person has it (keepr-api KPR-364): Installed, or Update available for a manager whose collection is behind. */}
				<InstalledBadge pkg={pkg} />
			</TileFoot>
			<MetaLine parts={meta} />
			{match ? <OverflowText className="discover-tile__match meta-line">{match}</OverflowText> : null}
		</Link>
	);
}

/**
 * One global collection (or keepr archive) on Discover: its identity links to
 * the collection itself — a global collection is read in place — and its
 * one action is Follow, which lists it under Global on the Collections page.
 */
export function GlobalCollectionTile({ row, onToggleFollow, busy = false, match = '' }) {
	const p = row.publication || {};
	const title = p.listing?.title || row.name;
	const archive = row.kind === 'archive';
	const meta = [];
	if (archive) {
		meta.push('keepr archive');
		if (p.retiredAt) meta.push(`retired ${formatDisplayDate(p.retiredAt)}`);
	} else {
		// Who publishes it (KPR-385) — and, where no publisher stands behind
		// it, the owner's name as before.
		if (row.publisher) meta.push(<PublishedBy publisher={row.publisher} />);
		else if (row.ownerName) meta.push(`By ${row.ownerName}`);
		if (p.followers > 0) meta.push(p.followers === 1 ? '1 follower' : `${p.followers.toLocaleString('en-US')} followers`);
		if (p.state === 'retiring' && p.retireAt) meta.push(`Retiring on ${formatDisplayDate(p.retireAt)}`);
	}
	const canFollow = !archive && p.state === 'published' && p.allowFollow !== false;
	return (
		<div className="card-def-card discover-tile discover-tile--global">
			<OverflowText className="discover-tile__title"><Link to={`/discover/global/${normalizeId(row._id)}`}>{title}</Link></OverflowText>
			{p.listing?.summary ? <OverflowText as="p" className="card-def-card__description">{p.listing.summary}</OverflowText> : null}
			{!archive && p.listing?.source?.text ? (
				// One line, the source's name only: its link and the license are
				// on the collection's About, where there is room to read them.
				<OverflowText className="discover-tile__source meta-line" data-testid="tile-source">Source: {p.listing.source.text}</OverflowText>
			) : null}
			<TileFoot kind="collection" records={[{ icon: row.icon, color: row.color, name: title }]} label={archive ? 'Archive' : 'Global collection'}>
				{canFollow || row.followed ? (
					<Button
						size="small"
						className="discover-tile__action"
						loading={busy}
						onClick={() => onToggleFollow(row)}
						aria-label={row.followed ? `Unfollow ${title}` : `Follow ${title}`}
					>
						{row.followed ? 'Following' : 'Follow'}
					</Button>
				) : null}
			</TileFoot>
			<MetaLine parts={meta} />
			{match ? <OverflowText className="discover-tile__match meta-line">{match}</OverflowText> : null}
		</div>
	);
}

const FACET_PARAMS = ['category', 'kind', 'examples', 'publisher'];
const CLOSEST_LIMIT = 6;

const rowId = row => (row?.type === 'collection' ? `collection:${normalizeId(row._id)}` : `package:${row?.key}`);

/**
 * The facets and the sort: one set of antd controls, drawn in the toolbar on
 * a wide screen and stacked in the Filters sheet on a phone.
 */
function DiscoverFacets({ facets, sort, searching, categories, publishers, onFacet, onSort, stacked = false }) {
	const size = stacked ? 'large' : 'middle';
	const selectClass = stacked ? 'discover-filters__control' : 'list-toolbar__select discover-facet';
	return (
		<>
			<Select
				className={selectClass}
				size={size}
				allowClear
				placeholder="Category"
				aria-label="Category"
				value={facets.category || undefined}
				options={categories.map(name => ({ value: name, label: name }))}
				onChange={value => onFacet('category', value || '')}
				popupMatchSelectWidth={false}
			/>
			<Select
				className={selectClass}
				size={size}
				allowClear
				placeholder="Kind"
				aria-label="Kind"
				value={facets.kind || undefined}
				options={KIND_OPTIONS}
				onChange={value => onFacet('kind', value || '')}
				popupMatchSelectWidth={false}
			/>
			<Select
				className={selectClass}
				size={size}
				allowClear
				placeholder="Publisher"
				aria-label="Publisher"
				value={facets.publisher || undefined}
				options={publishers}
				onChange={value => onFacet('publisher', value || '')}
				popupMatchSelectWidth={false}
			/>
			<Checkbox
				className={stacked ? 'discover-filters__check' : 'discover-facet discover-facet--check'}
				checked={facets.examples}
				onChange={event => onFacet('examples', event.target.checked ? '1' : '')}
			>
				Has examples
			</Checkbox>
			<Select
				className={stacked ? 'discover-filters__control' : 'list-toolbar__sort'}
				size={size}
				aria-label="Sort"
				value={sort}
				options={sortOptions(searching).map(option => ({ value: option.value, label: option.label }))}
				onChange={onSort}
				popupMatchSelectWidth={false}
			/>
		</>
	);
}

/**
 * Nothing matched: one line saying so, the closest matches when the facets
 * were what emptied it, and Build your own card last. Never a blank page.
 */
function NoResults({ search, filtered, closest, onClear, renderRow }) {
	const navigate = useNavigate();
	const line = search
		? `Nothing on Discover matches “${search}”${filtered ? ' with these filters' : ''}.`
		: filtered ? 'No packages match these filters.' : 'Nothing is published on Discover yet.';
	return (
		<div className="discover-empty">
			<div className="list-empty">
				<span>{line}</span>
				{filtered ? <Button onClick={onClear}>Clear filters</Button> : null}
			</div>
			{closest.length ? (
				<section className="discover-section" aria-label="Closest matches">
					<GroupHeader as="h2" label="Closest matches" count={closest.length} countLabel="listing" />
					<div className="card-def-grid">{closest.map(renderRow)}</div>
				</section>
			) : null}
			<div className="discover-empty__build">
				<span>Not here? Make the card yourself.</span>
				<Button onClick={() => navigate('/cards/new')}>Build your own card</Button>
			</div>
		</div>
	);
}

/**
 * /discover — the catalog of free packages (keepr-web docs/plans/
 * personal-discover-global.md §3.3): ready-made collections, card bundles and
 * cards, each a door to its own page where it is added to a collection.
 *
 * KPR-1437: the search box sends the words as `?search=` (keepr-api's ranked
 * search, best match first, each tile saying why it matched; global
 * collections in the same list). Category, Kind, Publisher and Has examples
 * narrow it and a Sort orders it, each in the address so a copied link
 * reproduces the view. Without words and sorted by Featured the page is the
 * curated catalog: the Featured row, then the categories. Every result is
 * reachable: Load more pages by `skip` until `X-Total-Count`.
 */
export default function Discover() {
	const { message } = App.useApp();
	const screens = Grid.useBreakpoint();
	const isMobile = screens.md === false;
	const [searchParams, setSearchParams] = useSearchParams();
	const setParamsRef = useRef(setSearchParams);
	setParamsRef.current = setSearchParams;
	const paramsKey = searchParams.toString();

	const search = (searchParams.get('search') || '').trim();
	const legacyQ = (searchParams.get('q') || '').trim();
	const sortParam = searchParams.get('sort') || '';
	const facets = useMemo(() => readFacets(new URLSearchParams(paramsKey)), [paramsKey]);
	const searching = Boolean(search);
	const sort = effectiveSort(sortParam, searching);
	const ranked = searching || sort !== 'featured';
	const requestQ = facetsToQuery(facets, legacyQ);
	const filtered = Boolean(requestQ);
	const sentSort = requestSort(sort, searching);

	/** Writes params into the address (a filter edit is not a history entry); '' deletes one. */
	const updateParams = useCallback(changes => {
		setParamsRef.current(prev => {
			const next = new URLSearchParams(prev);
			Object.entries(changes).forEach(([name, value]) => {
				if (value) next.set(name, value);
				else next.delete(name);
			});
			return next;
		}, { replace: true });
	}, []);

	// An older link's `?q=text ~ "words"` is a search now: its words move to
	// `?search=`, any other condition stays in `q`.
	useEffect(() => {
		if (!legacyQ) return;
		const { ast } = parseForBuilder(legacyQ);
		if (!ast) return;
		const term = readSearchTerm(ast, 'text');
		if (!term) return;
		const rest = withSearchTerm(ast, '', 'text');
		updateParams({ search: search || term, q: rest ? serialize(rest) : '' });
	}, [legacyQ, search, updateParams]);

	// ---- the rows: one page at a time, appended by Load more ----
	const [rows, setRows] = useState([]);
	const [total, setTotal] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [loadError, setLoadError] = useState(null);
	const [reloadKey, setReloadKey] = useState(0);
	const [learned, setLearned] = useState([]);
	const publishersSeen = useRef(new Map());
	const [listCategories, setListCategories] = useState(null);
	useEffect(() => {
		let live = true;
		getCategories().then(list => { if (live) setListCategories(list); }).catch(() => { if (live) setListCategories([]); });
		return () => { live = false; };
	}, []);

	const requestRef = useRef({});
	requestRef.current = { search, q: requestQ, sort: sentSort };
	const generationRef = useRef(0);
	useEffect(() => {
		const generation = ++generationRef.current;
		setLoading(true);
		setLoadError(null);
		searchDiscover({ search, q: requestQ, sort: sentSort })
			.then(({ rows: page, total: count }) => {
				if (generation !== generationRef.current) return;
				setRows(page);
				setTotal(count);
				// The category picker, when the server has no list: learned from
				// an unnarrowed read so narrowing never empties it.
				if (!search && !requestQ) setLearned([...new Set(page.map(row => row.category).filter(Boolean))]);
			})
			.catch(error => {
				if (generation !== generationRef.current) return;
				if (requestQ && error?.response?.status === 400) {
					message.error(error.response?.data?.message || 'That filter could not be read, so it was removed.');
					updateParams({ q: '', category: '', kind: '', examples: '', publisher: '' });
					return;
				}
				setLoadError(error);
			})
			.finally(() => { if (generation === generationRef.current) setLoading(false); });
		return () => { generationRef.current += 1; };
	}, [search, requestQ, sentSort, reloadKey, message, updateParams]);

	const hasMore = typeof total === 'number' && rows.length < total;
	const loadMore = useCallback(() => {
		const generation = generationRef.current;
		setLoadingMore(true);
		searchDiscover({ ...requestRef.current, skip: rows.length })
			.then(({ rows: page, total: count }) => {
				if (generation !== generationRef.current) return;
				setRows(prev => {
					const held = new Set(prev.map(rowId));
					return [...prev, ...page.filter(row => !held.has(rowId(row)))];
				});
				setTotal(count);
			})
			.catch(() => { if (generation === generationRef.current) message.error('More results could not be loaded. Try again.'); })
			.finally(() => { if (generation === generationRef.current) setLoadingMore(false); });
	}, [rows.length, message]);

	const categories = listCategories && listCategories.length ? listCategories : learned;
	const publishers = publisherOptions(rows, publishersSeen.current, facets.publisher);

	// ---- Global collections on the curated page (plan §4.7). A search
	// brings them in its own ranked list; a facet is about packages, so they
	// step aside while one is set. An API without them simply shows none. ----
	const curatedGlobals = !ranked && !filtered;
	const [globals, setGlobals] = useState([]);
	const [globalsLoading, setGlobalsLoading] = useState(true);
	const [globalsRead, setGlobalsRead] = useState(false);
	const [followBusy, setFollowBusy] = useState(null);
	useEffect(() => {
		let live = true;
		if (!curatedGlobals) { setGlobals([]); setGlobalsLoading(false); setGlobalsRead(true); return undefined; }
		setGlobalsLoading(true);
		listGlobalCollections({})
			.then(({ rows: list }) => { if (live) setGlobals(list); })
			.catch(() => { if (live) setGlobals([]); })
			.finally(() => { if (live) { setGlobalsLoading(false); setGlobalsRead(true); } });
		return () => { live = false; };
	}, [curatedGlobals]);
	const toggleFollow = useCallback(async row => {
		const id = normalizeId(row._id);
		setFollowBusy(id);
		try {
			if (row.followed) await unfollowCollection(id);
			else await followCollection(id);
			const flip = list => list.map(r => (r.type !== 'package' && normalizeId(r._id) === id ? { ...r, followed: !row.followed } : r));
			setGlobals(flip);
			setRows(flip);
			message.success(row.followed ? 'Unfollowed.' : 'Following — it is listed under Global on your Collections page.');
		} catch (e) {
			message.error(publicationRefusal(e));
		} finally {
			setFollowBusy(null);
		}
	}, [message]);
	// ---- Browse rows (KPR-1438): Most added, Recently updated and Goes with
	// what you have, under Featured on the unnarrowed page. Each is read
	// apart; a row with nothing in it is not drawn. ----
	const [browse, setBrowse] = useState({ mostAdded: [], recentlyUpdated: [], goesWith: [] });
	useEffect(() => {
		let live = true;
		if (!curatedGlobals) return undefined;
		loadBrowseRows().then(next => { if (live) setBrowse(next); });
		return () => { live = false; };
	}, [curatedGlobals, reloadKey]);
	const browseRows = curatedGlobals ? [
		{ id: 'most-added', label: 'Most added', rows: browse.mostAdded, line: () => '' },
		{ id: 'recently-updated', label: 'Recently updated', rows: browse.recentlyUpdated, line: () => '' },
		{ id: 'goes-with', label: 'Goes with what you have', rows: browse.goesWith, line: goesWithLine },
	].filter(row => row.rows.length) : [];
	const globalRows = useMemo(() => globals.filter(row => row.kind !== 'archive'), [globals]);
	const archiveRows = globals.filter(row => row.kind === 'archive');

	// ---- the search box: `?search=`, settled for a moment as the person types ----
	const [searchText, setSearchText] = useState(search);
	const searchTimerRef = useRef(null);
	useEffect(() => { if (!searchTimerRef.current) setSearchText(search); }, [search]);
	useEffect(() => () => clearTimeout(searchTimerRef.current), []);
	// A sort only words allow (Best match) leaves with them.
	const applySearch = useCallback(term => updateParams({ search: `${term || ''}`.trim(), ...(`${term || ''}`.trim() ? {} : { sort: sortParam === 'relevance' ? '' : sortParam }) }), [updateParams, sortParam]);
	const handleSearchChange = event => {
		const value = event.target.value;
		setSearchText(value);
		clearTimeout(searchTimerRef.current);
		if (!value.trim()) { searchTimerRef.current = null; applySearch(''); return; }
		searchTimerRef.current = setTimeout(() => { searchTimerRef.current = null; applySearch(value); }, SEARCH_DEBOUNCE_MS);
	};

	const setFacet = useCallback((name, value) => updateParams({ [name]: value }), [updateParams]);
	// The default sort is never written: a link without `sort` is Best match
	// with words and the curated page without.
	const setSort = useCallback(value => updateParams({ sort: value === (searching ? 'relevance' : 'featured') ? '' : value }), [updateParams, searching]);
	const clearFilters = useCallback(() => updateParams({ q: '', ...Object.fromEntries(FACET_PARAMS.map(name => [name, ''])) }), [updateParams]);

	// ---- the closest matches, when the facets emptied a search ----
	const [closest, setClosest] = useState([]);
	const emptied = !loading && !loadError && total === 0 && searching && filtered;
	useEffect(() => {
		let live = true;
		setClosest([]);
		if (!emptied) return undefined;
		searchDiscover({ search, limit: CLOSEST_LIMIT })
			.then(({ rows: list }) => { if (live) setClosest(list.slice(0, CLOSEST_LIMIT)); })
			.catch(() => { if (live) setClosest([]); });
		return () => { live = false; };
	}, [emptied, search]);

	// ---- the filters sheet (a phone) ----
	const [sheetOpen, setSheetOpen] = useState(false);
	const setCount = facetCount(facets);

	// ---- tiles ----
	const renderRankedRow = row => {
		const match = matchLine(row);
		if (row.type === 'collection') {
			const id = normalizeId(row._id);
			return <GlobalCollectionTile key={rowId(row)} row={row} onToggleFollow={toggleFollow} busy={followBusy === id} match={match} />;
		}
		// A bundle preset's own words matched: its tile opens that preset.
		const listing = row.listing ? (row.listings || []).find(entry => entry.key === row.listing) || null : null;
		return (
			<PackageTile
				key={rowId(row)}
				pkg={row}
				listing={listing}
				to={packagePath(row.key, listing ? { listing: listing.key } : undefined)}
				match={match}
			/>
		);
	};
	// Ranked by words: a tile per row, in the answer's order. Sorted without
	// words: a tile per listing, as on the curated page.
	const rankedTiles = searching
		? rows.map(renderRankedRow)
		: rows.flatMap(discoverTiles).map(tile => <PackageTile key={tile.id} pkg={tile.pkg} listing={tile.listing} to={tile.to} />);

	// The curated layout: Featured, then the categories in the admin's order.
	const entries = useMemo(() => (ranked ? [] : [
		// A tile per package — or, for a bundle with listing presets (keepr-api
		// KPR-383), a tile per listing, under its category.
		...rows.flatMap(discoverTiles).map(tile => ({
			id: `package:${tile.id}`,
			category: tile.pkg.category,
			featuredOrder: tile.pkg.featured ? (tile.pkg.featuredOrder || 0) : null,
			node: <PackageTile key={tile.id} pkg={tile.pkg} listing={tile.listing} to={tile.to} />,
		})),
		...globalRows.map(row => {
			const p = row.publication || {};
			const id = normalizeId(row._id);
			return {
				id: `collection:${id}`,
				category: p.listing?.category,
				// Only a collection that is still published holds a place in the row.
				featuredOrder: p.featured && p.state === 'published' ? (p.featuredOrder || 0) : null,
				node: <GlobalCollectionTile key={id} row={row} onToggleFollow={toggleFollow} busy={followBusy === id} />,
			};
		}),
	]), [ranked, rows, globalRows, toggleFollow, followBusy]);
	// The Featured row is the front page, so it shows on the unnarrowed view only.
	const featured = filtered ? [] : entries.filter(e => e.featuredOrder !== null).sort((a, b) => a.featuredOrder - b.featuredOrder);
	const listed = categories;
	const sections = [
		...listed.map(name => ({ name, rows: entries.filter(e => sameCategory(e.category, name)) })),
		{ name: OTHER, rows: entries.filter(e => !listed.some(name => sameCategory(e.category, name))) },
	].filter(section => section.rows.length);
	const unavailable = loadError?.response?.status === 404;
	const isEmpty = !rows.length && (ranked || !globals.length);
	const hasData = total !== null && globalsRead;

	const facetControls = stacked => (
		<DiscoverFacets
			facets={facets}
			sort={sort}
			searching={searching}
			categories={categories}
			publishers={publishers}
			onFacet={setFacet}
			onSort={setSort}
			stacked={stacked}
		/>
	);
	const countWords = typeof total === 'number'
		? (searching ? (total === 1 ? '1 result' : `${total} results`) : (total === 1 ? '1 package' : `${total} packages`))
		: null;

	return (
		<>
			<ContextBar title="Discover">
				<div className="list-toolbar list-toolbar--in-bar discover-toolbar">
					<Input
						{...NO_TEXT_ASSIST}
						className="list-toolbar__search"
						allowClear
						prefix={<MAGNIFYING_GLASS />}
						placeholder="Search Discover…"
						aria-label="Search Discover"
						value={searchText}
						onChange={handleSearchChange}
						onPressEnter={() => { clearTimeout(searchTimerRef.current); searchTimerRef.current = null; applySearch(searchText); }}
					/>
					{isMobile ? (
						<Button
							className="discover-filters__open"
							icon={<FILTER />}
							onClick={() => setSheetOpen(true)}
							aria-haspopup="dialog"
							aria-expanded={sheetOpen}
						>
							Filters{setCount ? <Badge count={setCount} size="small" className="discover-filters__count" /> : null}
						</Button>
					) : facetControls(false)}
					{filtered && !isMobile && !isEmpty ? <Button type="text" onClick={clearFilters}>Clear filters</Button> : null}
					<span className="list-toolbar__spacer" />
					{countWords ? <span className="list-toolbar__count">{countWords}</span> : null}
				</div>
			</ContextBar>

			{isMobile ? (
				<Drawer
					placement="bottom"
					height="auto"
					open={sheetOpen}
					onClose={() => setSheetOpen(false)}
					title="Filters"
					className="discover-filters-sheet"
				>
					<div className="discover-filters">
						{facetControls(true)}
						<div className="discover-filters__actions">
							{filtered ? <Button size="large" onClick={clearFilters}>Clear filters</Button> : null}
							<Button size="large" type="primary" onClick={() => setSheetOpen(false)}>
								{countWords ? `Show ${countWords}` : 'Show results'}
							</Button>
						</div>
					</div>
				</Drawer>
			) : null}

			{unavailable ? (
				<div className="list-empty"><span>Discover is not available on this server yet.</span></div>
			) : (
				<DataState
					state={deriveDataState({ loading: loading || globalsLoading, error: loadError, hasData, isEmpty })}
					hasData={hasData}
					isEmpty={isEmpty}
					skeleton={<Skeleton variant="tile" count={6} />}
					empty={(
						<NoResults
							search={search}
							filtered={filtered}
							closest={closest}
							onClear={clearFilters}
							renderRow={renderRankedRow}
						/>
					)}
					error={loadError ? 'Check your connection and try again.' : null}
					onRetry={() => setReloadKey(n => n + 1)}
					label="Discover"
				>
					{ranked ? (
						<section className="discover-section discover-section--ranked" aria-label={searching ? 'Results' : 'Packages'}>
							<div className="card-def-grid">{rankedTiles}</div>
						</section>
					) : (
						<>
							{featured.length ? (
								<section className="discover-section discover-section--featured" aria-label="Featured">
									<GroupHeader as="h2" label="Featured" count={featured.length} countLabel="listing" />
									<div className="card-def-grid">{featured.map(entry => entry.node)}</div>
								</section>
							) : null}
							{browseRows.map(row => (
								<section key={row.id} className="discover-section discover-section--browse" aria-label={row.label}>
									<GroupHeader as="h2" label={row.label} count={row.rows.length} countLabel="package" />
									<div className="card-def-grid">
										{row.rows.map(pkg => <PackageTile key={`${row.id}:${pkg.key}`} pkg={pkg} to={packagePath(pkg.key)} match={row.line(pkg)} />)}
									</div>
								</section>
							))}
							{sections.map(section => (
								<section key={section.name} className="discover-section" aria-label={section.name}>
									<GroupHeader as="h2" label={section.name} count={section.rows.length} countLabel="listing" />
									<div className="card-def-grid">{section.rows.map(entry => entry.node)}</div>
								</section>
							))}
							{archiveRows.length ? (
								<section className="discover-section" aria-label="Archive">
									<GroupHeader as="h2" label="Archive" count={archiveRows.length} countLabel="collection" />
									<div className="card-def-grid">
										{archiveRows.map(row => <GlobalCollectionTile key={normalizeId(row._id)} row={row} onToggleFollow={toggleFollow} />)}
									</div>
								</section>
							) : null}
						</>
					)}
					{hasMore ? (
						<div className="discover-more">
							<Button onClick={loadMore} loading={loadingMore}>
								Load more
							</Button>
							<span className="discover-more__count">{`Showing ${rows.length} of ${total}`}</span>
						</div>
					) : null}
				</DataState>
			)}
		</>
	);
}

